import { BaseApiService } from './base.service';

export interface BoletoEligibilityResponse {
  eligible: boolean;
  currency: string;
  currency_is_brl: boolean;
  customer_has_tax_id: boolean;
  customer_tax_id: string | null;
  invoice_amount: number;
  invoice_status: string;
  existing_boleto: {
    boleto_payment_id: string;
    payment_token: string;
    status: string;
    created_at: string;
    boleto_code?: string;
    boleto_duedate?: string;
    amount?: number;
    currency?: string;
    expiration_date?: string;
  } | null;
}

export interface BoletoCreationResponse {
  success: boolean;
  message: string;
  boleto_payment_id: string | null;
  payment_token: string | null;
}

export interface BoletoExistsResponse {
  success: false;
  message: string;
  existing_boleto: {
    id: string;
    payment_token: string;
    reference_id: string;
    transaction_status: string;
    boleto_code: string;
    amount: number;
    currency: string;
    created_at: string;
    expiration_date?: string;
    boleto_duedate?: string;
  };
  requires_confirmation: true;
  suggestion?: string;
}

export type BoletoCreateResult = BoletoCreationResponse | BoletoExistsResponse;

export interface BoletoStatusResponse {
  success: boolean;
  boleto_payment_id: string;
  payment_token: string;
  status: string;
  amount: number;
  due_date: string;
  created_at: string;
  updated_at: string;
}

/**
 * Service for managing boleto payments
 */
export class BoletoService extends BaseApiService {
  /**
   * Check if an invoice is eligible for boleto payment
   */
  async checkBoletoEligibility(invoiceId: string): Promise<BoletoEligibilityResponse> {
    return this.get(`/api/v1/boleto/check/${invoiceId}`);
  }

  /**
   * Create a boleto payment for an invoice
   */
  async createBoletoForInvoice(
    invoiceId: string,
    notificationUrl: string = 'https://your-domain.com/api/v1/boleto/webhook/epag',
    forceCreate: boolean = false,
    dueDate?: string
  ): Promise<BoletoCreateResult> {
    // Validate due date is provided
    if (!dueDate) {
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 5);
      dueDate = defaultDate.toISOString();
      console.warn('Due date not provided, using default (5 days from now):', dueDate);
    }

    const payload: any = { 
      notification_url: notificationUrl,
      force_create: forceCreate,
      due_date: dueDate
    };
    
    console.log('=== BOLETO SERVICE API CALL ===');
    console.log('Invoice ID:', invoiceId);
    console.log('Payload being sent to API:', JSON.stringify(payload, null, 2));
    console.log('API Endpoint:', `/api/v1/boleto/create/${invoiceId}`);
    console.log('===============================');
    
    try {
      const result: BoletoCreateResult = await this.post(`/api/v1/boleto/create/${invoiceId}`, payload);
      console.log('=== BOLETO SERVICE API RESPONSE ===');
      console.log('Response:', JSON.stringify(result, null, 2));
      console.log('===================================');
      return result;
    } catch (error) {
      console.error('=== BOLETO SERVICE API ERROR ===');
      console.error('Error:', error);
      console.error('================================');
      throw error;
    }
  }

  /**
   * Get boleto payment status for an invoice
   */
  async getBoletoStatus(invoiceId: string): Promise<BoletoStatusResponse> {
    return this.get(`/api/v1/boleto/status/${invoiceId}`);
  }

  async sendUpdatedBoleto(invoiceId: string): Promise<{ success: boolean; email_id?: string }> {
    return this.post(`/api/v1/boleto/send-updated/${invoiceId}`, {});
  }

  /**
   * Generate invoice PDF with boleto included if applicable
   */
  async generateInvoiceWithBoletoPDF(
    invoiceId: string,
    language: string = 'pt-br',
    createBoleto: boolean = true,
    forceCreate: boolean = false,
    notificationUrl?: string
  ): Promise<Blob> {
    const params = new URLSearchParams({
      language,
      create_boleto: createBoleto.toString(),
      force_create: forceCreate.toString()
    });
    
    if (notificationUrl) {
      params.append('notification_url', notificationUrl);
    }

    return this.getBlob(`/api/v1/boleto/invoice/${invoiceId}/pdf?${params.toString()}`);
  }

  /**
   * Get URL for invoice PDF with boleto
   */
  getInvoiceWithBoletoPDFUrl(
    invoiceId: string,
    language: string = 'pt-br',
    createBoleto: boolean = true,
    forceCreate: boolean = false,
    notificationUrl?: string
  ): string {
    const params = new URLSearchParams({
      language,
      create_boleto: createBoleto.toString(),
      force_create: forceCreate.toString()
    });
    
    if (notificationUrl) {
      params.append('notification_url', notificationUrl);
    }

    const baseUrl = this.getBaseUrl();
    return `${baseUrl}/api/v1/boleto/invoice/${invoiceId}/pdf?${params.toString()}`;
  }

  /**
   * Download invoice PDF with boleto
   */
  async downloadInvoiceWithBoletoPDF(
    invoiceId: string,
    language: string = 'pt-br',
    createBoleto: boolean = true,
    forceCreate: boolean = false,
    notificationUrl?: string,
    filename?: string
  ): Promise<void> {
    const blob = await this.generateInvoiceWithBoletoPDF(invoiceId, language, createBoleto, forceCreate, notificationUrl);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `invoice-${invoiceId}-with-boleto.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Open invoice PDF with boleto in new tab
   */
  async openInvoiceWithBoletoPDF(
    invoiceId: string,
    language: string = 'pt-br',
    createBoleto: boolean = true,
    forceCreate: boolean = false,
    notificationUrl?: string
  ): Promise<void> {
    const url = this.getInvoiceWithBoletoPDFUrl(invoiceId, language, createBoleto, forceCreate, notificationUrl);
    window.open(url, '_blank');
  }

  /**
   * Regenerate invoice PDF with existing boleto
   */
  async regenerateInvoiceWithExistingBoleto(
    invoiceId: string,
    language: string = 'pt-br'
  ): Promise<Blob> {
    const params = new URLSearchParams({ language });
    return this.getBlob(`/api/v1/boleto/invoice/${invoiceId}/regenerate-pdf?${params.toString()}`);
  }

  /**
   * Get boleto analytics
   */
  async getBoletoAnalytics(period: string = '30d'): Promise<{
    success: boolean;
    data: {
      total_boletos: number;
      completed_boletos: number;
      pending_boletos: number;
      expired_boletos: number;
      completion_rate: number;
      total_revenue: number;
      period: string;
      date_range: {
        start: string;
        end: string;
      };
    };
    timestamp: string;
  }> {
    return this.get(`/api/v1/boleto/analytics?period=${period}`);
  }
}

export const boletoService = new BoletoService();